import React, { useState } from "react";
import Joi from "joi";
import PropTypes from "prop-types";
import "./renew-subscription.m.css";
import { useSelector } from "react-redux";
import get from "lodash/get";

const ReNewSubscriptionForm = ({ selectedPlanId }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [loading, setLoading] = useState(false);

  const qtState = useSelector((state) => get(state, ["qt"]));
  const SITE_KEY = get(qtState, ["config", "publisher-attributes", "ccavenue", "google_recaptcha_site_key"], false);

  const schema = Joi.object({
    name: Joi.string().trim().required().messages({
      "string.empty": "Name is required",
    }),
    email: Joi.string()
      .trim()
      .email({ tlds: { allow: false } })
      .required()
      .messages({
        "string.empty": "Email is required",
        "string.email": "Enter a valid email address",
      }),
    phone: Joi.string()
      .trim()
      .pattern(/^\d{10}$/)
      .required()
      .messages({
        "string.empty": "Phone is required",
        "string.pattern.base": "Please enter a valid 10-digit number",
      }),
  });

  const updateField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validateForm = () => {
    const { error } = schema.validate(form, { abortEarly: false });
    if (!error) return null;

    const validationErrors = {};
    for (const err of error.details) {
      validationErrors[err.path[0]] = err.message;
    }
    return validationErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError("");

    const validationErrors = validateForm();
    if (validationErrors) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);

    try {
      // ✅ Run Google reCAPTCHA v3
      const token = await window.grecaptcha.execute(SITE_KEY, {
        action: "renew_subscription",
      });

      // ✅ Call backend API with POST and reCAPTCHA token in body
      const response = await fetch("/subscriptions/renew", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          subscription_package: selectedPlanId,
          recaptcha_token: token,
        }),
      });

      const result = await response.json();

      if (!result.success) {
        setApiError(result.error || result.message || "Unable to process renewal");
        setLoading(false);
        return;
      }

      const subscription = result.data || result;

      if (subscription.ccavenue.encRequest && subscription.ccavenue.access_code) {
        const formEl = document.createElement("form");
        formEl.method = "POST";
        formEl.action = subscription.ccavenue.action_url;

        const encInput = document.createElement("input");
        encInput.name = "encRequest";
        encInput.value = subscription.ccavenue.encRequest;
        formEl.appendChild(encInput);

        const accessInput = document.createElement("input");
        accessInput.name = "access_code";
        accessInput.value = subscription.ccavenue.access_code;
        formEl.appendChild(accessInput);

        document.body.appendChild(formEl);
        formEl.submit();
      } else {
        setApiError("Missing CCAvenue data in response.");
      }
    } catch (err) {
      console.error("API Error:", err);
      setApiError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div styleName="renew-form-wrapper">
      <form styleName="renew-form" onSubmit={handleSubmit}>
        <div styleName="renew-field">
          <label styleName="renew-label" htmlFor="name">
            Name
          </label>
          <input
            styleName="renew-input"
            id="name"
            name="name"
            type="text"
            value={form.name}
            onChange={(e) => updateField("name", e.target.value)}
          />
          {errors.name && <span styleName="renew-error">{errors.name}</span>}
        </div>

        <div styleName="renew-field">
          <label styleName="renew-label" htmlFor="email">
            Email ID
          </label>
          <input
            styleName="renew-input"
            id="email"
            name="email"
            type="email"
            value={form.email}
            onChange={(e) => updateField("email", e.target.value)}
          />
          {errors.email && <span styleName="renew-error">{errors.email}</span>}
        </div>

        <div styleName="renew-field">
          <label styleName="renew-label" htmlFor="phone">
            Phone
          </label>
          <input
            styleName="renew-input"
            id="phone"
            name="phone"
            type="tel"
            value={form.phone}
            onChange={(e) => updateField("phone", e.target.value)}
          />
          {errors.phone && <span styleName="renew-error">{errors.phone}</span>}
        </div>

        {apiError && (
          <div styleName="renew-error" style={{ textAlign: "center" }}>
            {apiError}
          </div>
        )}

        <button styleName="renew-continue" type="submit" disabled={loading}>
          {loading ? "Please wait..." : "Continue"}
        </button>
      </form>
    </div>
  );
};

ReNewSubscriptionForm.propTypes = {
  selectedPlanId: PropTypes.string,
};

export default ReNewSubscriptionForm;
